// frontend/src/hooks/usePayrollReadiness.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import toast from '../utils/toast';

/**
 * Fetch the unified payroll readiness dashboard data.
 *
 * @param {string} period         - YYYY-MM format (e.g. "2026-02")
 * @param {string[]} departmentIds - Array of department UUIDs; empty array = all departments
 * @param {object} [options]      - Extra React Query options
 */
export const usePayrollReadiness = (period, departmentIds = [], options = {}) => {
  const deptKey = Array.isArray(departmentIds) ? departmentIds.slice().sort().join(',') : '';
  return useQuery({
    queryKey: ['payroll', 'readiness', period, deptKey],
    queryFn: async () => {
      const params = { period };
      const ids = Array.isArray(departmentIds) ? departmentIds.filter(Boolean) : [];
      if (ids.length > 0) {
        params.department_ids = ids.join(',');
      }
      const response = await api.get('/payroll/readiness/', { params });
      return response.data;
    },
    enabled: !!period,
    staleTime: 2 * 60 * 1000, // 2 minutes — readiness data changes as HR works
    ...options,
  });
};

/**
 * Trigger a period-wide exception detection sweep (Celery task).
 * Used by the "Scan for Exceptions" button on the Readiness Dashboard.
 */
export const useScanExceptions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (period) => {
      // This calls the Celery task via a thin API trigger
      const response = await api.post('/exceptions/scan/', { period });
      return response.data;
    },
    onSuccess: (data, period) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'readiness'] });
      const total = data?.total_exceptions_created ?? 0;
      if (total > 0) {
        toast.success(`Scan complete — ${total} new exception(s) detected`);
      } else {
        toast.success('Scan complete — no new exceptions found');
      }
    },
    onError: () => {
      toast.error('Exception scan failed. Please try again.');
    },
  });
};
